<template>
  <div class="rounded shadow-xs">
    <div class="rounded bg-white shadow-lg">
      <BaseVeeForm
        :action="submit"
        ensure-error-keys-camel-case
        v-slot="formProps"
        class="space-y-8"
      >
        <div class="p-4 lg:p-6 bg-gray-100 rounded-t">
          <h3 class="text-lg">Project Product</h3>
          <p class="text-sm text-gray-600">Change the product used on this project</p>
        </div>
        <div class="p-4 lg:p-6 space-y-4 lg:space-y-6">
          <ValidationProvider name="product" slim vid="productId" rules="required">
            <template #default="{ errors }">
              <BaseFormGroup>
                <VSelect
                  v-model="formData.productId"
                  :options="productOptions"
                  label="name"
                  :reduce="(product) => product.id"
                  :filterable="false"
                  :disabled="formProps.isPending"
                  @search="onProductSearch"
                  class="vue-select custom-option sm:w-1/2"
                >
                  <template #option="{ name }">
                    <div class="flex group px-4 py-2 items-center">
                      <p class="text-sm font-medium text-gray-900 group-hover:text-white">
                        {{ name }}
                      </p>
                    </div>
                  </template>
                </VSelect>
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>
        </div>
        <div class="p-4 lg:p-6">
          <BaseButton
            :primary="formProps.isIdle || formProps.isPending"
            :success="formProps.isResolved"
            :danger="formProps.isRejected"
            :disabled="formProps.invalid"
          >
            <span v-if="formProps.isIdle">Submit</span>
            <span v-else-if="formProps.isPending">Please wait...</span>
            <span v-else-if="formProps.isResolved">Success!</span>
            <span v-else>Failed!</span>
          </BaseButton>
        </div>
      </BaseVeeForm>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import { productsClient, projectsClient } from '@/api/clients';
import mapKeysToCamelCase from '@/utils/map-keys-to-camel-case';
import mapKeysToSnakeCase from '@/utils/map-keys-to-snake-case';

export default {
  name: 'ProjectProductForm',
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      formData: {
        productId: this.project.productId || null,
      },
      productOptions: [],
    };
  },
  computed: {
    product() {
      if (!this.project.productId) {
        return null;
      }

      return this.$store.state.entities.products.byId[this.project.productId] || null;
    },
  },
  created() {
    this.fetchProducts({
      filter: {
        search: this.product?.name || '',
      },
    });
  },
  methods: {
    async submit() {
      try {
        await projectsClient.update(this.project.uuid, mapKeysToSnakeCase(this.formData));

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async onProductSearch(search, loading) {
      if (search.length) {
        loading(true);
        this.searchProducts(loading, search, this);
      }
    },
    searchProducts: debounce(async (loading, search, vm) => {
      try {
        await vm.fetchProducts({
          filter: {
            search,
          },
        });
      } catch (error) {
        //
      } finally {
        loading(false);
      }
    }, 500),
    async fetchProducts(query = {}) {
      try {
        const response = await productsClient.list(query);

        this.setProductOptions(response.data.map(mapKeysToCamelCase));

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setProductOptions(products) {
      this.productOptions = products;
    },
  },
};
</script>
